import React, { useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, Input } from "reactstrap";

// Um lado da rota de cobertura (PO1-3648, épico PO1-3359): UF inteira ou uma
// cidade. Recebe as funções de carga por props porque a página pública usa os
// endpoints anônimos (/public/rede-everlog/...) e o MeuFrete os autenticados
// (/meufrete/rede/...): o componente não sabe de HTTP.
//
// - `carregarEstados()`  → [{ id, sigla, nome }]
// - `carregarCidades(uf, q)` → [{ id, nome, idEstado, uf }] (q com 2+ letras)
// - `value`: { modo: 'uf'|'cidade', idEstado, uf, idCidade, cidade }
// - `onChange(valorNovo)`
//
// Controles nativos (Input select/text) de propósito: funcionam no glass-card
// da página pública e no tema claro/escuro da área logada, e são testáveis com
// Simulate.change. A busca de cidade tem debounce de 250 ms, como no Sourcing.

const DEBOUNCE_MS = 250;
const MIN_LETRAS = 2;

const listaStyle = {
  position: "absolute",
  zIndex: 20,
  left: 0,
  right: 0,
  marginTop: 2,
  maxHeight: 220,
  overflowY: "auto",
  borderRadius: 10,
  background: "var(--everlog-card-surface-flat, #1e1e2f)",
  border: "1px solid var(--everlog-card-border, rgba(255,255,255,0.15))",
  boxShadow: "var(--everlog-card-shadow, 0 8px 24px rgba(0,0,0,0.35))",
};
const opcaoStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: "0.4rem 0.7rem",
  fontSize: "0.86rem",
  color: "var(--everlog-card-text, inherit)",
  cursor: "pointer",
};

export default function CidadeUfSelect({ value, onChange, carregarEstados, carregarCidades, estados: estadosProp, disabled, rotulo, idPrefixo }) {
  const v = value || { modo: "uf", idEstado: null, uf: "", idCidade: null, cidade: "" };
  const [estadosLocal, setEstadosLocal] = useState([]);
  const estados = estadosProp || estadosLocal;
  const [busca, setBusca] = useState("");
  const [opcoes, setOpcoes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const timer = useRef(null);

  // Só carrega os estados aqui quando o pai não os passou (o CoberturaEditor
  // carrega uma vez e repassa para todas as linhas).
  useEffect(() => {
    if (estadosProp || typeof carregarEstados !== "function") return undefined;
    let ativo = true;
    Promise.resolve(carregarEstados())
      .then((lista) => {
        if (ativo) setEstadosLocal(Array.isArray(lista) ? lista : []);
      })
      .catch(() => {
        if (ativo) setEstadosLocal([]);
      });
    return () => {
      ativo = false;
    };
  }, [estadosProp, carregarEstados]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const emitir = (parcial) => {
    if (onChange) onChange({ ...v, ...parcial });
  };

  const trocarModo = (modo) => {
    if (modo === v.modo) return;
    // Trocar o modo limpa a cidade; a UF fica, porque a cidade é buscada nela.
    setBusca("");
    setOpcoes([]);
    emitir({ modo, idCidade: null, cidade: "" });
  };

  const trocarUf = (idTexto) => {
    const e = estados.find((x) => String(x.id) === String(idTexto));
    setBusca("");
    setOpcoes([]);
    emitir({ idEstado: e ? e.id : null, uf: e ? e.sigla : "", idCidade: null, cidade: "" });
  };

  const buscarCidades = (texto) => {
    setBusca(texto);
    clearTimeout(timer.current);
    if (v.idCidade) emitir({ idCidade: null, cidade: "" });
    if (!v.uf || String(texto).trim().length < MIN_LETRAS || typeof carregarCidades !== "function") {
      setOpcoes([]);
      return;
    }
    setBuscando(true);
    timer.current = setTimeout(async () => {
      try {
        const lista = await carregarCidades(v.uf, String(texto).trim());
        setOpcoes(Array.isArray(lista) ? lista : []);
        setAberto(true);
      } catch (_) {
        setOpcoes([]);
      } finally {
        setBuscando(false);
      }
    }, DEBOUNCE_MS);
  };

  const escolherCidade = (c) => {
    setBusca("");
    setOpcoes([]);
    setAberto(false);
    emitir({ idCidade: c.id, cidade: c.nome, idEstado: c.idEstado || v.idEstado, uf: c.uf || v.uf });
  };

  const prefixo = idPrefixo || "lado";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      {rotulo ? <div style={{ fontSize: "0.74rem", opacity: 0.8 }}>{rotulo}</div> : null}
      <ButtonGroup size="sm" aria-label={`${rotulo || "Lado"}: UF ou cidade`}>
        <Button type="button" color="info" outline={v.modo !== "uf"} onClick={() => trocarModo("uf")} disabled={disabled} aria-pressed={v.modo === "uf"}>
          UF
        </Button>
        <Button type="button" color="info" outline={v.modo !== "cidade"} onClick={() => trocarModo("cidade")} disabled={disabled} aria-pressed={v.modo === "cidade"}>
          Cidade
        </Button>
      </ButtonGroup>
      <Input type="select" bsSize="sm" aria-label={`${rotulo || "Lado"}: UF`} data-testid={`${prefixo}-uf`} value={v.idEstado || ""} onChange={(e) => trocarUf(e.target.value)} disabled={disabled}>
        <option value="">UF</option>
        {estados.map((e) => (
          <option key={e.id} value={e.id}>
            {e.sigla}
          </option>
        ))}
      </Input>
      {v.modo === "cidade" ? (
        <div style={{ position: "relative" }}>
          <Input
            type="text"
            bsSize="sm"
            aria-label={`${rotulo || "Lado"}: cidade`}
            data-testid={`${prefixo}-cidade`}
            placeholder={v.uf ? "Digite a cidade" : "Escolha a UF antes"}
            value={v.idCidade ? v.cidade : busca}
            onChange={(e) => buscarCidades(e.target.value)}
            onFocus={() => setAberto(true)}
            onBlur={() => setTimeout(() => setAberto(false), 150)}
            disabled={disabled || !v.uf}
            autoComplete="off"
          />
          {aberto && (buscando || opcoes.length > 0 || (busca.length >= MIN_LETRAS && !v.idCidade)) ? (
            <div style={listaStyle} role="listbox">
              {buscando ? <div style={{ ...opcaoStyle, cursor: "default", opacity: 0.7 }}>Buscando…</div> : null}
              {!buscando && opcoes.length === 0 ? <div style={{ ...opcaoStyle, cursor: "default", opacity: 0.7 }}>Nenhuma cidade encontrada</div> : null}
              {opcoes.map((c) => (
                <button key={c.id} type="button" role="option" aria-selected={false} style={opcaoStyle} onMouseDown={(ev) => ev.preventDefault()} onClick={() => escolherCidade(c)}>
                  {c.nome}
                  {c.uf ? <span style={{ opacity: 0.6 }}> / {c.uf}</span> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
